import { ChevronLeft, Loader2, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea, ScrollAreaViewport, ScrollBar } from '@/components/ui/scroll-area';
import { useCompany } from '@/contexts/CompanyContext';
import { type GetPromisesToPayParams, usePromisesList } from '@/hooks/usePromisesToPayData';
import { getPersistedFilters } from '@/lib/session-storage-filters';
import { cn, formatDate } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils/formatters';
import { PROMISES_LIST_FILTERS_KEY } from '@/pages/collections/promises';
import type { PromiseToPay } from '@/types/collections';

const SIDEBAR_PAGE_SIZE = 25;

interface PromiseDetailsSidebarProps {
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function PromiseDetailsSidebar({ isCollapsed = false, onToggleCollapse }: PromiseDetailsSidebarProps) {
  const { selectedCompany } = useCompany();
  const navigate = useNavigate();
  const { promiseId } = useParams<{ promiseId: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [accumulatedPromises, setAccumulatedPromises] = useState<PromiseToPay[]>([]);
  const prevDataRef = useRef<string>('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page and accumulated promises when search changes
  useEffect(() => {
    setPage(1);
    setAccumulatedPromises([]);
    prevDataRef.current = '';
  }, [debouncedSearch]);

  // Read persisted filters from the promises list page
  const persistedFilters = useMemo(() => {
    const stored = getPersistedFilters<{
      advancedFilters: {
        statuses: string[];
        assignedUser: string;
        currencies: string[];
        dueToday: boolean;
        createdAtFrom: string;
        createdAtTo: string;
      };
      searchQuery: string;
    }>(PROMISES_LIST_FILTERS_KEY);
    return stored?.advancedFilters ?? null;
  }, []);

  // Fetch promises with pagination, optional search, and persisted filters
  const queryParams = useMemo(() => {
    const params: GetPromisesToPayParams = {
      page,
      pageSize: SIDEBAR_PAGE_SIZE,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    };
    if (persistedFilters) {
      if (persistedFilters.statuses.length > 0) params.statuses = persistedFilters.statuses;
      if (persistedFilters.assignedUser && persistedFilters.assignedUser !== 'all') {
        const userId = Number(persistedFilters.assignedUser);
        if (!isNaN(userId)) params.assignedUserId = userId;
      }
      if (persistedFilters.currencies.length > 0) params.currencyCodes = persistedFilters.currencies;
      if (persistedFilters.dueToday) {
        const today = new Date().toISOString().split('T')[0];
        params.dateFrom = today;
        params.dateTo = today;
      }
      if (persistedFilters.createdAtFrom) params.createdAtFrom = persistedFilters.createdAtFrom;
      if (persistedFilters.createdAtTo) params.createdAtTo = persistedFilters.createdAtTo;
    }
    return params;
  }, [page, debouncedSearch, persistedFilters]);

  const { data, isLoading } = usePromisesList(queryParams);
  const fetchedPromises = useMemo(() => data?.promises ?? [], [data?.promises]);
  const hasNextPage = data?.pagination.hasNextPage ?? false;

  // Accumulate promises when new data arrives
  useEffect(() => {
    if (fetchedPromises.length === 0) return;

    const dataKey = fetchedPromises.map((p) => `${p.id}:${p.promisedAmount}`).join(',');
    if (dataKey === prevDataRef.current) return;
    prevDataRef.current = dataKey;

    if (page === 1) {
      setAccumulatedPromises(fetchedPromises);
    } else {
      setAccumulatedPromises((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        const newPromises = fetchedPromises.filter((p) => !existingIds.has(p.id));
        return [...prev, ...newPromises];
      });
    }
  }, [fetchedPromises, page]);

  // Handle empty results for page 1
  useEffect(() => {
    if (!isLoading && fetchedPromises.length === 0 && page === 1) {
      setAccumulatedPromises([]);
    }
  }, [isLoading, fetchedPromises.length, page]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isLoading) {
      setPage((prev) => prev + 1);
    }
  }, [hasNextPage, isLoading]);

  const handlePromiseClick = (selectedPromiseId: string) => {
    navigate(`/collections/promises/${selectedPromiseId}`);
  };

  const getStatusColor = (_status: PromiseToPay['status'], isSelected: boolean) => {
    if (isSelected) {
      return 'border-l-teal-500';
    }
    return 'border-l-gray-300 dark:border-l-gray-700';
  };

  const getStatusBadgeVariant = (status: PromiseToPay['status']) => {
    switch (status) {
      case 'Active':
        return 'default';
      case 'Paid':
        return 'secondary';
      case 'Broken':
        return 'destructive';
      case 'Partially Paid':
        return 'default';
      case 'Closed':
        return 'outline';
      default:
        return 'default';
    }
  };

  const isLoadingFirstPage = isLoading && page === 1;
  const isLoadingMore = isLoading && page > 1;

  return (
    <div className="relative h-full flex flex-col">
      {!isCollapsed && (
        <Card className="h-full border-r rounded-none flex flex-col shadow-md">
          <CardHeader className="pb-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Promises to Pay</CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleCollapse}
                className="h-8 w-8 rounded-full hover:bg-muted"
                title="Close sidebar"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
            <div className="relative mt-2">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, number, or case..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0 flex-1 min-h-0">
            <ScrollArea className="h-full w-full">
              <ScrollAreaViewport className="h-full">
                <div className="space-y-1 px-3 pb-3">
                  {isLoadingFirstPage ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : accumulatedPromises.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      {searchQuery ? 'No promises found' : 'No promises available'}
                    </div>
                  ) : (
                    <>
                      {accumulatedPromises.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => handlePromiseClick(p.id)}
                          className={cn(
                            'w-full text-left p-3 rounded-lg border-l-4 transition-all hover:bg-muted/50',
                            getStatusColor(p.status, p.id === promiseId),
                            p.id === promiseId
                              ? 'bg-teal-50 dark:bg-teal-950/30 shadow-sm'
                              : 'bg-background hover:shadow-sm',
                          )}
                        >
                          <div className="space-y-1.5">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm truncate">{p.id}</p>
                                <p className="text-xs text-muted-foreground truncate">{p.customerName}</p>
                              </div>
                              <Badge variant={getStatusBadgeVariant(p.status)} className="text-xs shrink-0">
                                {p.status}
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-bold text-teal-600 dark:text-teal-400">
                                {formatCurrency(p.promisedAmount, p.currencyCode || selectedCompany?.currency || 'GBP')}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatDate(p.promiseDate.toISOString())}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground">Case: {p.caseNumber}</div>
                          </div>
                        </button>
                      ))}
                      {hasNextPage && (
                        <button
                          onClick={handleLoadMore}
                          disabled={isLoadingMore}
                          className="w-full py-3 text-center text-sm font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 hover:bg-muted/50 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {isLoadingMore ? (
                            <span className="flex items-center justify-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Loading...
                            </span>
                          ) : (
                            'Load more'
                          )}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </ScrollAreaViewport>
              <ScrollBar orientation="vertical" />
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
